/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/icon_paused_gray.svg";
import __asset_1 from "@res/icons/icon_play_gray.svg";
import * as React from "react";
import * as emotion from "emotion";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {WaveSurferComponent} from "./WaveSurferComponent";
import {COLORS} from "@res/colors";
import {
	generateHex,
	Second
} from "@intuitionrobotics/ts-common";
import WaveSurfer from "wavesurfer.js";
import {ICONS} from "@res/icons";
import {
	OnMediaPlayed,
	OnStopAllPlayers,
	PlayerModule
} from "@modules/PlayerModule";

export const icon_paused_gray = __asset_0;
export const icon_play_gray = __asset_1;

type Props = {
	url?: string | HTMLMediaElement;
	// Blob has priority over URL.
	blob?: Blob | File;
	trashAudio?: () => void;
	isSending?: boolean
};

type State = {
	ready: boolean;
	waveSurfer?: WaveSurfer;
	playing: boolean;
	currentTimeSeconds?: number;
	duration?: number;
	id: string
};

const audioPlayerStyle = emotion.css(
	{
		backgroundColor: "white"
	});

const buttonStyle = emotion.css(
	{
		width: 32,
		height: 32,
		marginRight: 8
	}
);

const currentTimeStyle = emotion.css(
	{
		marginRight: 8,
		width: 60,
		boxSizing: "border-box"
	}
);

const duration = emotion.css(
	{
		marginLeft: 8,
		width: 60,
		boxSizing: "border-box"
	}
);

export class AudioWavePlayer
	extends BaseComponent<Props, State>
  implements OnMediaPlayed, OnStopAllPlayers {

	__onMediaPlayed = (playerId: string) => {
		if (this.state.id === playerId || !this.state.playing) {
			return;
		}
		this.state.waveSurfer?.stop()
		this.onFinish()
	}

	__onStopAllPlayers = () => {
		if (!this.state.playing) {
			return;
		}

		this.state.waveSurfer?.stop()
		this.onFinish()
	}

	private audioPlayerRef?: HTMLDivElement;
	private throttleInterval: number = 0.05 * Second;

	constructor(props: Props) {
		super(props);
		this.state = {
			id: generateHex(32),
			ready: false,
			waveSurfer: undefined,
			playing: false,
			currentTimeSeconds: undefined,
			duration: undefined
		};
	}

	waveSurferInit = (waveSurfer: WaveSurfer) => {
		if (this.props.blob)
			waveSurfer.loadBlob(this.props.blob);
		else if (this.props.url)
			waveSurfer.load(this.props.url);
		else
			return this.logError("Missing some usable prop. Please pass either a blob or a url");

		this.setState({waveSurfer}, () => {
			waveSurfer.on("finish", this.onFinish);
			waveSurfer.on("audioprocess", this.onAudioProcess);
			waveSurfer.on("seek", this.onSeek);
		});
	};

	onWaveSurferReady = () => {
		this.logInfo("waveSurfer instance is ready, parent call");
		if (!this.state.waveSurfer)
			return;

		this.setState(
			{
				ready: true,
				duration: this.state.waveSurfer.getDuration()
			});
	};

	onWaveSurferError = (error?: any) => {
		this.logError("Error event:", error);
	};

	onFinish = () => {
		if (!this.state.waveSurfer) {
			// This should not happen.
			this.logError("WaveSurfer instance not defined on finish.");
			return;
		}

		this.logInfo("Playing finished");
		this.state.waveSurfer.seekTo(0);
		this.setState({playing: false});
	};

	private onAudioProcess = (e: number): void => {
		return this.throttle(() => this.onAudioProcessImpl(e), "audio-process", this.throttleInterval);
	};

	private onAudioProcessImpl = (sec: number) => {
		if (!this.state.waveSurfer) {
			this.setState({currentTimeSeconds: 0});
			return;
		}

		// This is required so that we don't change currentTimeSeconds after finishing.
		// For some reason an audioprocess event seems to be fired after the finish event.
		if (!this.state.waveSurfer.isPlaying())
			return;

		this.setState({currentTimeSeconds: sec});
	};

	// TODO: Investigate the events triggered when seeking while playing.
	private onSeek = (_progress: any) => {
		if (!this.state.waveSurfer) {
			// This should not happen.
			this.logError("WaveSurfer instance not defined on seek.");
			return;
		}

		if (this.state.waveSurfer.isPlaying())
			return;

		const currentTime = this.state.waveSurfer.getCurrentTime();
		this.setState(() => ({currentTimeSeconds: currentTime}));
	};

	private togglePlay = () => {
		if (!this.state.waveSurfer || !this.state.ready)
			return;

		this.setState({playing: !this.state.playing}, () => {
			              if (this.state.playing)
				              PlayerModule.notifyPlayers(this.state.id);
		              }
		);

		this.state.waveSurfer.playPause()?.catch(this.logError);

	};

	private prettyCurrentTime = (sec?: number) => {
		if (!sec)
			return "0:00";

		const secInt = Math.floor(sec);
		const minutes = Math.floor(secInt / 60);
		const formattedNumber = (secInt % 60).toLocaleString("en-US", {
			minimumIntegerDigits: 2,
			useGrouping: false
		});

		return `${minutes}:${formattedNumber}`;
	};

	render() {
		let style = {borderRadius: "31.5px", height: "100%", width: "100%"};

		if (!this.props.isSending)
			style = {...style, ...{boxSizing: "border-box", border: `1px solid ${COLORS.neutral_gray_2}`, padding: "8px 13px"}}

		return <div id={`audio-player-${generateHex(16)}`}
		            style={style}
		            ref={(ref) => {
			            if (!ref || this.audioPlayerRef)
				            return;

			            this.audioPlayerRef = ref;
			            this.forceUpdate();
		            }}
		            className={`ll_h_c ${audioPlayerStyle}`}>
			{this.renderButton()}
			{this.renderCurrentTime()}
			{this.renderWaveSurfer()}
			{this.renderDuration()}
			{this.props.isSending && this.renderDeleteAudio()}
		</div>;
	}

	private renderButton = () => {
		const button = this.state.playing ?
			<img alt={"pause"} src={icon_paused_gray}/> :
			<img alt={"play"} src={icon_play_gray}/>;

		return <div className={`${buttonStyle}`}
		            onClick={this.togglePlay}>
			{button}
		</div>;
	};

	private renderCurrentTime = () => {
		return <div className={`${currentTimeStyle}`}>
			{this.prettyCurrentTime(this.state.currentTimeSeconds)}
		</div>;
	};

	private renderDuration = () => {
		return <div className={`${duration}`}>
			{this.prettyCurrentTime(this.state.duration)}
		</div>;
	};

	private renderWaveSurfer = () => {
		if (!this.audioPlayerRef)
			return;

		const computerStyle = getComputedStyle(this.audioPlayerRef);

		const waveSurferHeight = this.audioPlayerRef.clientHeight -
			parseFloat(computerStyle.paddingTop) -
			parseFloat(computerStyle.paddingBottom);

		return <div className={`match_width match_height`}>
			<WaveSurferComponent
				wavesurferParams={{
					height: waveSurferHeight,
					barHeight: 1.5,
					cursorWidth: 0,
					backgroundColor: "white",
					progressColor: COLORS.neutral_text_dark_2,
					waveColor: COLORS.neutral_gray_2,
					barWidth: 2,
					barGap: 1,
					barMinHeight: 1,
					hideScrollbar: true
				}}
				placeholder={<div>Loading...</div>}
				onWaveSurferInit={this.waveSurferInit}
				onReady={this.onWaveSurferReady}
				onError={this.onWaveSurferError}
			/>
		</div>;
	};

	private renderDeleteAudio() {
		return <div
			style={{cursor: "pointer"}}
			onClick={() => {
				if (this.props.trashAudio) {
					if (this.state.playing)
						this.togglePlay();

					this.props.trashAudio();
				}
			}}>{ICONS.trash_can(COLORS.black, 30)}</div>;
	}
}
